import type { WorkflowSpecification } from '@/defaults/workflowSpecification'

/**
 * Resolve the status to display from a document's workflow index fields.
 *
 * A checkpoint only outranks the workflow state when that checkpoint is a non
 * workflow status. For tt/print-article that is 'usable' (Exporterad). It is in
 * workflow_state only while the exported version is the latest, after further edits
 * the state falls back to draft but the checkpoint stays.
 *
 * 'unpublished' is written as a checkpoint by the repository as well, but is never
 * cleared when the document moves on. Trusting it would pin the document to Inställd
 * for the rest of its life, so it is skipped by name. Name, not spec, since it is
 * isWorkflow: false for core/event, core/planning-item and core/factbox.
 *
 * Follows the same rule as getStatusFromMeta applies to document meta in
 * useWorkflowStatus.
 *
 * Takes the specification for a single document type rather than the type name,
 * so that callers rendering many rows resolve getWorkflowSpecifications() once
 * instead of per row. Building that object is not cheap, it translates every
 * title and description for every known type.
 */
export function getWorkflowStatusName({ workflow, workflowState, workflowCheckpoint }: {
  workflow: WorkflowSpecification | undefined
  workflowState?: string | null
  workflowCheckpoint?: string | null
}): string {
  // hasOwn keeps a checkpoint named after something on Object.prototype, say
  // 'constructor', from resolving to anything at all
  const checkpointSpec = (workflowCheckpoint && workflow && Object.hasOwn(workflow, workflowCheckpoint))
    ? workflow[workflowCheckpoint]
    : undefined

  if (
    workflowCheckpoint
    && workflowCheckpoint !== 'unpublished'
    && checkpointSpec?.isWorkflow === false
  ) {
    return workflowCheckpoint
  }

  return workflowState || 'draft'
}
